const DROP_CONTENT_TAGS = new Set(["script", "style", "template", "iframe", "object", "embed"]);

const DEFAULT_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "blockquote",
  "code",
  "pre",
  "ul",
  "ol",
  "li",
  "span",
  "div",
  "img",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
];

const SERVICE_DESCRIPTION_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "blockquote",
  "ul",
  "ol",
  "li",
  "span",
  "a",
];

const DEFAULT_ALLOWED_ATTRIBUTES = {
  "*": ["style"],
  a: ["href", "title", "target", "rel"],
  img: ["src", "alt", "title", "width", "height", "style"],
};

const toSetMap = (attributes = DEFAULT_ALLOWED_ATTRIBUTES) =>
  Object.entries(attributes).reduce((acc, [tag, attrs]) => {
    acc[tag] = new Set(attrs);
    return acc;
  }, {});

const toSet = (items) => new Set(items || []);

const isSafeUrl = (url = "", options = {}) => {
  const value = url.trim().toLowerCase();
  if (!value) return false;
  if (value.startsWith("/") || value.startsWith("#")) return true;
  if (options.allowDataImages && value.startsWith("data:image/")) return true;
  return ["http:", "https:", "mailto:", "tel:"].some((protocol) => value.startsWith(protocol));
};

const SAFE_FONT_SIZE_UNIT_REGEX = /^\d{1,3}(\.\d+)?(px|rem|em|%|vw|vh)$/i;
const SAFE_FONT_SIZE_CLAMP_SEGMENT_REGEX =
  /^(?:0|(?:\d{1,3}(?:\.\d+)?(?:px|rem|em|%|vw|vh)))(?:\s*[+-]\s*(?:\d{1,3}(?:\.\d+)?(?:px|rem|em|%|vw|vh)))*$/i;

const normalizeFontSizeValue = (value = "") => {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed || /[;{}]/.test(trimmed)) return null;

  if (SAFE_FONT_SIZE_UNIT_REGEX.test(trimmed)) {
    return trimmed;
  }

  const clampMatch = trimmed.match(/^clamp\((.*)\)$/i);
  if (!clampMatch) return null;

  const parts = clampMatch[1]
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length !== 3) return null;
  if (!parts.every((part) => SAFE_FONT_SIZE_CLAMP_SEGMENT_REGEX.test(part))) {
    return null;
  }

  return `clamp(${parts.join(", ")})`;
};

const sanitizeStyleValue = (value = "", options = {}) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const allowedProps = {
    color: /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i,
    "background-color": /^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i,
    "font-weight": /^(normal|bold|bolder|lighter|[1-9]00)$/i,
    "font-style": /^(normal|italic|oblique)$/i,
    "text-decoration": /^(none|underline|line-through|overline)$/i,
  };

  if (options.allowFontSize) {
    allowedProps["font-size"] = normalizeFontSizeValue;
  }

  const safePairs = trimmed
    .split(";")
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [prop, ...rest] = pair.split(":");
      const name = prop ? prop.trim().toLowerCase() : "";
      const rawValue = rest.join(":").trim();
      const rule = allowedProps[name];
      if (!rule) return null;

      if (typeof rule === "function") {
        const normalizedValue = rule(rawValue);
        if (!normalizedValue) return null;
        return `${name}: ${normalizedValue}`;
      }

      if (!rule.test(rawValue)) return null;
      return `${name}: ${rawValue}`;
    })
    .filter(Boolean);

  return safePairs.join("; ");
};

const isSafeDimension = (value = "") => /^(\d{1,4}(\.\d+)?)(px|%)?$/.test(value.trim());

const sanitizeNode = (node, allowedTags, allowedAttributes, options = {}) => {
  if (node.nodeType === Node.TEXT_NODE) return;

  if (node.nodeType === Node.ELEMENT_NODE) {
    const tagName = node.nodeName.toLowerCase();

    if (!allowedTags.has(tagName)) {
      const parent = node.parentNode;
      if (!parent) {
        // Root containers (like the temporary wrapper <div>) should still sanitize descendants.
        Array.from(node.childNodes).forEach((child) =>
          sanitizeNode(child, allowedTags, allowedAttributes, options)
        );
        return;
      }

      if (DROP_CONTENT_TAGS.has(tagName)) {
        parent.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach((child) => parent.insertBefore(child, node));
      parent.removeChild(node);
      // The parent is walking the children it had before this node was
      // unwrapped, so it never reaches the ones just moved up. Clean them
      // here, or <font><img onerror></font> would come through untouched.
      children.forEach((child) => sanitizeNode(child, allowedTags, allowedAttributes, options));
      return;
    }

    const attrsForTag = allowedAttributes[tagName] || allowedAttributes["*"] || new Set();
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (!attrsForTag.has(name)) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "style") {
        const sanitized = sanitizeStyleValue(attr.value, options);
        if (!sanitized) {
          node.removeAttribute(attr.name);
        } else {
          node.setAttribute("style", sanitized);
        }
        return;
      }

      if (name === "width" || name === "height") {
        if (!isSafeDimension(attr.value)) {
          node.removeAttribute(attr.name);
        }
        return;
      }

      if (name === "href" && !isSafeUrl(attr.value)) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "src" && !isSafeUrl(attr.value, { allowDataImages: tagName === "img" })) {
        node.removeAttribute(attr.name);
        return;
      }

      if (name === "target" && !["_self", "_blank"].includes(attr.value)) {
        node.removeAttribute(attr.name);
      }
    });

    if (tagName === "a" && node.getAttribute("target") === "_blank") {
      const rel = node.getAttribute("rel") || "";
      const relParts = rel
        .split(/\s+/)
        .filter(Boolean)
        .reduce((acc, value) => acc.add(value.toLowerCase()), new Set());

      relParts.add("noopener");
      relParts.add("noreferrer");
      node.setAttribute("rel", Array.from(relParts).join(" "));
    }
  }

  Array.from(node.childNodes).forEach((child) =>
    sanitizeNode(child, allowedTags, allowedAttributes, options)
  );
};

export const sanitizeHtml = (dirty, options = {}) => {
  if (!dirty) return "";
  if (typeof document === "undefined") {
    return typeof dirty === "string" ? dirty : "";
  }

  const allowedTags = toSet(options.allowedTags || DEFAULT_ALLOWED_TAGS);
  const allowedAttributes = toSetMap(options.allowedAttributes);
  const sanitizerOptions = {
    allowFontSize: options.allowFontSize === true,
  };

  // Parse in a document with no browsing context. There, markup loads nothing
  // and runs no handler, so an <img onerror> can't fire before it is stripped;
  // a div in the live page would start loading it right away.
  const container = document.implementation.createHTMLDocument("").createElement("div");
  container.innerHTML = dirty;
  sanitizeNode(container, allowedTags, allowedAttributes, sanitizerOptions);
  return container.innerHTML;
};

export const createSanitizedMarkup = (dirty, options = {}) => ({
  __html: sanitizeHtml(dirty, options),
});

export const createSanitizedServiceMarkup = (dirty) =>
  createSanitizedMarkup(dirty, { allowedTags: SERVICE_DESCRIPTION_ALLOWED_TAGS });

export { SERVICE_DESCRIPTION_ALLOWED_TAGS };
